import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Radio, Sparkles, Zap } from "lucide-react";
import { useI18n } from "@/i18n";

// Premium locked LIVE experience for plans without LIVE entitlement (FREE).
// Renders NO real score/minute/team/market data — only a static blurred
// skeleton illustration behind the upsell. Safe marketing metadata only.
function SkeletonCard() {
  return (
    <div className="panel relative overflow-hidden p-4" aria-hidden>
      <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-live/10 blur-3xl" />
      <div className="flex items-center justify-between">
        <div className="skeleton h-4 w-28 rounded" />
        <div className="skeleton h-5 w-12 rounded-md" />
      </div>
      <div className="mt-4 rounded-xl border border-atlas/15 bg-atlas/[0.04] p-3">
        <div className="skeleton h-3 w-20 rounded" />
        <div className="mt-2 skeleton h-5 w-32 rounded" />
      </div>
      <div className="mt-4 flex items-center justify-between">
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2"><div className="skeleton h-6 w-6 rounded-full" /><div className="skeleton h-4 w-24 rounded" /></div>
          <div className="flex items-center gap-2"><div className="skeleton h-6 w-6 rounded-full" /><div className="skeleton h-4 w-20 rounded" /></div>
        </div>
        <div className="skeleton h-9 w-14 rounded" />
      </div>
    </div>
  );
}

export function LiveLocked({ compact = false, teaserCount = null }: { compact?: boolean; teaserCount?: number | null }) {
  const { lang } = useI18n();
  const hasTeaser = typeof teaserCount === "number" && Number.isFinite(teaserCount) && teaserCount > 0;

  const overlay = (
    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 px-5 text-center">
      <span className="grid h-14 w-14 place-items-center rounded-2xl border border-gold/30 bg-gold/10 text-gold-300">
        <Lock className="h-6 w-6" />
      </span>
      <div className="max-w-md space-y-2">
        <h3 className="flex items-center justify-center gap-2 text-lg font-extrabold tracking-tight text-foreground sm:text-xl">
          <Radio className="h-4 w-4 text-live-400" /> {lang === "tr" ? "Canlı ATLAS Analizleri" : "Live ATLAS Analysis"}
        </h3>
        <p className="text-sm text-muted-foreground">
          {lang === "tr"
            ? "Canlı maç sinyalleri ve anlık ATLAS değerlendirmeleri PRO ile açılır."
            : "Live match signals and real-time ATLAS evaluations unlock with PRO."}
        </p>
        {hasTeaser && (
          <p className="inline-flex items-center gap-1.5 rounded-full border border-live/25 bg-live/10 px-3 py-1 text-xs font-semibold text-live-300" data-testid="live-locked-teaser-count">
            <Zap className="h-3 w-3" /> {lang === "tr"
              ? `ATLAS şu anda ${teaserCount} canlı maçı takip ediyor.`
              : `ATLAS is currently tracking ${teaserCount} live matches.`}
          </p>
        )}
      </div>
      <Link
        to="/app/membership"
        data-testid="live-locked-cta"
        className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-atlas-500 to-atlas-600 px-5 py-2.5 text-sm font-bold text-atlas-ink transition hover:shadow-glow"
      >
        <Sparkles className="h-4 w-4" /> {lang === "tr" ? "PRO ile Aç" : "Unlock with PRO"}
      </Link>
    </div>
  );

  if (compact) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="relative overflow-hidden rounded-2xl border border-gold/20"
        data-testid="live-locked"
      >
        <div className="pointer-events-none select-none blur-[3px] opacity-40">
          <SkeletonCard />
        </div>
        {overlay}
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative overflow-hidden rounded-2xl border border-gold/20"
      data-testid="live-locked"
    >
      <div className="pointer-events-none select-none grid gap-4 p-1 blur-[4px] opacity-35 sm:grid-cols-2 xl:grid-cols-3">
        {[0, 1, 2, 3, 4, 5].map((i) => <SkeletonCard key={i} />)}
      </div>
      {overlay}
    </motion.div>
  );
}
